import { invoke } from '@tauri-apps/api/core'
import workletUrl from './nativeSpeakerWorklet.js?url'

// macOS: call audio is played by Rust (`commands/native_speaker.rs`), not by WebKit — WebKit lists
// no output devices without a `getUserMedia` grant (the mic is native, so there's none) and
// ignores `setSinkId` for WebRTC playback anyway, so «Вывод звука» could never work.
//
//   shared AudioContext: LiveKit's `webAudioMix` gain nodes -> `context.destination`
//     = this sink's AudioWorkletNode (the instance property is overridden) -> Rust -> CoreAudio
//
// Overriding `destination` catches everything that plays through the shared context (every remote
// participant, self-monitoring) without touching LiveKit. Outside a call — or if the native output
// fails to open — the node passes audio straight to the real destination, as if it weren't there.

export class NativeSpeakerSink {
  private speakerId: number | null = null
  private wanted = false

  private constructor(private readonly node: AudioWorkletNode) {
    node.port.onmessage = (event: MessageEvent<ArrayBuffer>) => {
      if (this.speakerId === null) return
      void invoke('native_speaker_write', new Uint8Array(event.data), {
        headers: { 'x-speaker-id': String(this.speakerId) },
      }).catch(() => undefined)
    }
  }

  /** Must run before anything connects to `context.destination`. */
  static async install(context: AudioContext): Promise<NativeSpeakerSink> {
    await context.audioWorklet.addModule(workletUrl)
    const node = new AudioWorkletNode(context, 'native-speaker-sink', {
      numberOfInputs: 1,
      numberOfOutputs: 1,
      outputChannelCount: [2],
      channelCount: 2,
      channelCountMode: 'explicit',
      channelInterpretation: 'speakers',
    })
    node.connect(context.destination)
    Object.defineProperty(context, 'destination', { value: node, configurable: true })
    return new NativeSpeakerSink(node)
  }

  /** Starts (or moves) native playback on `deviceId` — a CoreAudio name, or 'default'. */
  async start(deviceId: string) {
    this.wanted = true
    const next = await invoke<number>('native_speaker_start', {
      deviceName: deviceId === 'default' ? null : deviceId,
      sampleRate: this.node.context.sampleRate,
    })
    if (!this.wanted) {
      await invoke('native_speaker_stop', { speakerId: next }).catch(() => undefined)
      return
    }
    const previous = this.speakerId
    this.speakerId = next
    this.node.port.postMessage({ mode: 'native' })
    if (previous !== null) await invoke('native_speaker_stop', { speakerId: previous }).catch(() => undefined)
  }

  /** Back to WebKit playback. Idempotent. */
  async stop() {
    this.wanted = false
    this.node.port.postMessage({ mode: 'passthrough' })
    const previous = this.speakerId
    this.speakerId = null
    if (previous !== null) await invoke('native_speaker_stop', { speakerId: previous }).catch(() => undefined)
  }
}
